import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { fluxAPI } from './api';
import './ImageGenerator.css';

const ImageGenerator = ({ onImageGenerated, serverReady }) => {
  const [prompt, setPrompt] = useState('');
  const [width, setWidth] = useState(1024);
  const [height, setHeight] = useState(768);
  const [numSteps, setNumSteps] = useState(28);
  const [guidanceScale, setGuidanceScale] = useState(4.0);
  const [seed, setSeed] = useState('');
  const [referenceImages, setReferenceImages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState('');

  // 드래그 앤 드롭 설정
  const onDrop = useCallback((acceptedFiles) => {
    if (referenceImages.length + acceptedFiles.length > 4) {
      setError('참조 이미지는 최대 4개까지만 업로드 가능합니다.');
      return;
    }

    const newImages = acceptedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setReferenceImages(prev => [...prev, ...newImages]);
    setError(null);
  }, [referenceImages.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp']
    },
    maxFiles: 4,
    disabled: referenceImages.length >= 4
  });

  const removeReferenceImage = (index) => {
    setReferenceImages(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!prompt.trim()) {
      setError('프롬프트를 입력해주세요.');
      return;
    }

    if (!serverReady) {
      setError('서버가 준비되지 않았습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setProgress('이미지 생성 중...');

    try {
      const params = {
        prompt,
        width,
        height,
        num_inference_steps: numSteps,
        guidance_scale: guidanceScale
      };

      if (seed) {
        params.seed = seed;
      }

      let response;

      // 참조 이미지가 있으면 multipart, 없으면 JSON
      if (referenceImages.length > 0) {
        const files = referenceImages.map(img => img.file);
        response = await fluxAPI.generateImageWithReferences(params, files);
      } else {
        response = await fluxAPI.generateImage(params);
      }

      if (response.success) {
        setProgress('이미지 다운로드 중...');

        // ✨ 즉시 이미지를 다운로드하여 blob URL 생성
        try {
          const blobUrl = await fluxAPI.downloadImageAsBlob(response.filename);

          setProgress('생성 완료!');

          // 이미지 정보와 함께 blob URL도 전달
          onImageGenerated({
            ...response,
            prompt,
            width,
            height,
            timestamp: new Date().toISOString(),
            blobUrl // ✨ blob URL 추가
          });

          // 성공 메시지 2초 후 제거
          setTimeout(() => {
            setProgress('');
          }, 2000);
        } catch (downloadError) {
          console.error('이미지 다운로드 실패:', downloadError);
          setError('이미지는 생성되었지만 표시에 실패했습니다. 갤러리에서 다시 로드해주세요.');

          // 다운로드 실패해도 기본 정보는 전달
          onImageGenerated({
            ...response,
            prompt,
            width,
            height,
            timestamp: new Date().toISOString()
          });
        }
      } else {
        setError(response.error || '이미지 생성에 실패했습니다.');
      }
    } catch (err) {
      console.error('이미지 생성 오류:', err);
      if (err.response?.status === 401) {
        setError('인증이 만료되었습니다. Access Code를 다시 입력해주세요.');
      } else {
        setError(err.response?.data?.error?.message || err.message || '이미지 생성 중 오류가 발생했습니다.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // 사전 정의된 해상도
  const presetResolutions = [
    { name: '정사각형 (1:1)', width: 608, height: 608 },
    { name: '큰정사각형 (1:1)', width: 1024, height: 1024 },
    { name: '가로 (16:9)', width: 1024, height: 576 },
    { name: '세로 (9:16)', width: 768, height: 1344 },
    { name: '사진 (4:3)', width: 1024, height: 768 },
    { name: '초상화 (2:3)', width: 768, height: 1152 },
    { name: '배너', width: 1184, height: 256 },
  ];

  return (
    <div className="image-generator">
      <h2>이미지 생성</h2>

      <form onSubmit={handleSubmit}>
        {/* 프롬프트 입력 */}
        <div className="form-group">
          <label htmlFor="prompt">프롬프트 *</label>
          <textarea
            id="prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="생성하고 싶은 이미지를 설명해주세요..."
            rows={4}
            required
          />
          {referenceImages.length > 0 && (
            <small className="hint">
              💡 참조 이미지를 "image 1", "image 2" 등으로 지칭할 수 있습니다.
            </small>
          )}
        </div>

        {/* 참조 이미지 업로드 */}
        <div className="form-group">
          <label>참조 이미지 (선택사항, 최대 4개)</label>

          {referenceImages.length < 4 && (
            <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
              <input {...getInputProps()} />
              {isDragActive ? (
                <p>📂 이미지를 여기에 드롭하세요...</p>
              ) : (
                <p>📂 이미지를 드래그하거나 클릭하여 업로드</p>
              )}
            </div>
          )}

          {/* 업로드된 이미지 미리보기 */}
          {referenceImages.length > 0 && (
            <div className="reference-images">
              {referenceImages.map((img, index) => (
                <div key={index} className="reference-image-item">
                  <img src={img.preview} alt={`Reference ${index + 1}`} />
                  <div className="reference-image-overlay">
                    <span className="image-number">Image {index + 1}</span>
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeReferenceImage(index)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 해상도 설정 */}
        <div className="form-group">
          <label>해상도</label>
          <div className="resolution-presets">
            {presetResolutions.map((preset, index) => (
              <button
                key={index}
                type="button"
                className={`preset-btn ${width === preset.width && height === preset.height ? 'active' : ''}`}
                onClick={() => {
                  setWidth(preset.width);
                  setHeight(preset.height);
                }}
              >
                {preset.name}
                <br />
                <small>{preset.width}×{preset.height}</small>
              </button>
            ))}
          </div>

          <div className="resolution-inputs">
            <div>
              <label htmlFor="width">너비</label>
              <input
                id="width"
                type="number"
                value={width}
                onChange={(e) => setWidth(parseInt(e.target.value))}
                min={256}
                max={1440}
                step={32}
              />
            </div>
            <div>
              <label htmlFor="height">높이</label>
              <input
                id="height"
                type="number"
                value={height}
                onChange={(e) => setHeight(parseInt(e.target.value))}
                min={256}
                max={1440}
                step={32}
              />
            </div>
          </div>
        </div>

        {/* 고급 설정 */}
        <details className="advanced-settings">
          <summary>고급 설정</summary>

          <div className="form-group">
            <label htmlFor="numSteps">
              추론 단계 수: {numSteps}
              <small>(적을수록 빠름, 많을수록 품질 향상)</small>
            </label>
            <input
              id="numSteps"
              type="range"
              value={numSteps}
              onChange={(e) => setNumSteps(parseInt(e.target.value))}
              min={20}
              max={50}
              step={1}
            />
          </div>

          <div className="form-group">
            <label htmlFor="guidanceScale">
              가이던스 스케일: {guidanceScale}
              <small>(프롬프트 충실도)</small>
            </label>
            <input
              id="guidanceScale"
              type="range"
              value={guidanceScale}
              onChange={(e) => setGuidanceScale(parseFloat(e.target.value))}
              min={1}
              max={7}
              step={0.1}
            />
          </div>

          <div className="form-group">
            <label htmlFor="seed">
              시드 (선택사항)
              <small>(같은 시드는 같은 결과 생성)</small>
            </label>
            <input
              id="seed"
              type="number"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              placeholder="비워두면 랜덤"
            />
          </div>
        </details>

        {/* 오류 메시지 */}
        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        {/* 진행 상태 */}
        {progress && (
          <div className="progress-message">
            {progress}
          </div>
        )}

        {/* 생성 버튼 */}
        <button
          type="submit"
          className="generate-btn"
          disabled={isGenerating || !serverReady}
        >
          {isGenerating ? '생성 중...' : '🎨 이미지 생성'}
        </button>
      </form>
    </div>
  );
};

export default ImageGenerator;